/**
 * Workflows API client (SPEC PX-4 — workflow-builder-2026-06-17).
 *
 * Talks to the EXISTING `/api/workflows/*` CRUD surface (E5) plus the new
 * read-only `GET /api/workflows/metadata` palette descriptor. Mirrors the
 * self-contained fetch + Firebase-ID-token auth pattern from `lib/api/pipeline.ts`
 * (its own API_BASE_URL + getAuthHeader + handle<T>). No new auth or base-URL
 * pattern is invented.
 *
 * Save = POST (create, active:false) / PUT (update). Activation is PUT
 * {active:true} (no separate endpoint). Validation runs INSIDE POST/PUT (the
 * engine's WorkflowDefinitionModel.validate_semantics) and surfaces as a 422
 * whose `detail` the canvas renders.
 */

import { auth } from '../firebase';
import type {
  WorkflowCreate,
  WorkflowDefinition,
  WorkflowListItem,
  WorkflowMetadata,
  WorkflowUpdate,
} from '@/types/workflow';

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  'https://sales-api-768596480524.us-central1.run.app';

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }
  const token = await user.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function handle<T>(response: Response, what: string): Promise<T> {
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to ${what}: ${response.statusText}`);
  }
  return response.json();
}

/** GET /api/workflows — the tenant's workflow definitions (HMAC masked server-side). */
export async function listWorkflows(): Promise<WorkflowListItem[]> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/workflows`, { headers });
  const data = await handle<{ workflows: WorkflowListItem[]; count: number }>(
    response,
    'list workflows'
  );
  return data.workflows;
}

/** GET /api/workflows/{id} — a single definition (404/403 via the primitive). */
export async function getWorkflow(id: string): Promise<WorkflowDefinition> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/workflows/${id}`, { headers });
  const data = await handle<{ workflow: WorkflowDefinition }>(response, 'load workflow');
  return data.workflow;
}

/** GET /api/workflows/metadata — the registry-driven palette descriptor. */
export async function getWorkflowMetadata(): Promise<WorkflowMetadata> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/workflows/metadata`, { headers });
  return handle<WorkflowMetadata>(response, 'load workflow metadata');
}

/** POST /api/workflows — create (auto-validates; 422 on a bad shape). Always
 * `active:false` on first create (dead-by-default). */
export async function createWorkflow(body: WorkflowCreate): Promise<WorkflowDefinition> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/workflows`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ ...body, active: false }),
  });
  const data = await handle<{ workflow: WorkflowDefinition }>(response, 'create workflow');
  return data.workflow;
}

/** PUT /api/workflows/{id} — partial update (re-validates; also the activate path
 * via {active:true}). */
export async function updateWorkflow(
  id: string,
  body: WorkflowUpdate
): Promise<WorkflowDefinition> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/workflows/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  });
  const data = await handle<{ workflow: WorkflowDefinition }>(response, 'update workflow');
  return data.workflow;
}

/** DELETE /api/workflows/{id}. */
export async function deleteWorkflow(id: string): Promise<void> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/workflows/${id}`, {
    method: 'DELETE',
    headers,
  });
  await handle<{ deleted: boolean }>(response, 'delete workflow');
}
